import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Star } from 'lucide-react';
import { formatDate } from '@/lib/utils-helpers';

export default function VendorReviews() {
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [avgRating, setAvgRating] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/vendor/reviews');
        setReviews(res.data.reviews || []);
        setTotal(res.data.total_count || res.data.total || 0);
        setAvgRating(res.data.average_rating || 0);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const renderStars = (rating) => (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => <Star key={i} className={`h-3.5 w-3.5 ${i <= rating ? 'text-amber-500 fill-amber-500' : 'text-slate-200'}`} />)}
    </div>
  );

  if (loading) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-64" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">Customer Reviews</h1>
        <p className="text-muted-foreground mt-1">See what customers are saying about your store.</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="kpi-card">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center">
                <Star className="h-8 w-8 text-amber-500 fill-amber-500" />
              </div>
              <div>
                <p className="text-3xl font-bold tabular-nums" data-font="display">{avgRating}</p>
                <p className="text-sm text-muted-foreground">Average Rating</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center">
                <span className="text-2xl font-bold text-blue-600" data-font="display">{total}</span>
              </div>
              <div>
                <p className="text-3xl font-bold tabular-nums" data-font="display">{total}</p>
                <p className="text-sm text-muted-foreground">Total Reviews</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reviews List */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base font-medium">All Reviews</CardTitle></CardHeader>
        <CardContent>
          {reviews.length > 0 ? (
            <div className="space-y-3">
              {reviews.map(review => (
                <div key={review.id} className="p-4 bg-muted/30 rounded-xl border" data-testid={`vendor-review-${review.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-xs font-semibold text-primary">{review.reviewer_name?.[0] || 'C'}</span>
                      </div>
                      <div>
                        <span className="text-sm font-medium">{review.reviewer_name}</span>
                        <div className="flex items-center gap-2">
                          {renderStars(review.rating)}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">{formatDate(review.created_at)}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{review.comment}</p>
                </div>
              ))}
            </div>
          ) : <p className="text-center py-8 text-muted-foreground">No reviews yet</p>}
        </CardContent>
      </Card>
    </div>
  );
}
